import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";
import {
  getAccessToken,
  getCurrentUser,
  hasJwtSession,
  type JwtUser,
} from "@/lib/jwt-auth-client";
import { appEvents } from "@/shared/lib/events";

/**
 * Auth provider using Django JWT tokens.
 * Reads user info from the JWT access token payload.
 */

interface AuthUser {
  id: string;
  email: string;
  name: string;
  image?: string | null;
  createdAt?: Date;
  role?: string;
}

interface AuthSession {
  user: AuthUser;
}

interface AuthContextValue {
  data: AuthSession | null;
  isPending: boolean;
  error: unknown;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function toAuthUser(user: JwtUser): AuthUser {
  return {
    id: user.id,
    email: user.email,
    name: user.name,
    role: user.role,
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [jwtUser, setJwtUser] = useState<AuthUser | null>(null);
  const [jwtChecked, setJwtChecked] = useState(false);

  // Check for JWT session on mount
  useEffect(() => {
    if (!hasJwtSession()) {
      // eslint-disable-next-line no-console
      console.debug("[jwt-auth] AuthProvider: no session on mount");
      setJwtChecked(true);
      return;
    }

    // Try to get/refresh the JWT access token
    getAccessToken()
      .then((tok) => {
        const user = getCurrentUser();
        // eslint-disable-next-line no-console
        console.debug("[jwt-auth] AuthProvider: startup auth", {
          hadToken: !!tok,
          hasUser: !!user,
        });
        if (user) {
          setJwtUser(toAuthUser(user));
        }
        setJwtChecked(true);
      })
      .catch((e) => {
        console.error("[jwt-auth] AuthProvider: startup auth threw", e);
        setJwtChecked(true);
      });
  }, []);

  useLayoutEffect(() => {
    return appEvents.subscribe("auth:state-changed", ({ user }) => {
      setJwtUser(user ? toAuthUser(user) : null);
      setJwtChecked(true);
    });
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    if (jwtUser) {
      return {
        data: { user: jwtUser },
        isPending: false,
        error: null,
      };
    }

    return {
      data: null,
      isPending: !jwtChecked,
      error: null,
    };
  }, [jwtUser, jwtChecked]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return context;
}
